import React, { FC, useCallback, useState } from 'react';
import Select, { InputProps, Options, components } from 'react-select';
import { selectStyles } from '../selectValue/styles';
import { PlusButton } from 'shared/ui/buttons/iconButtons/plusButton';
import { MinusButton } from 'shared/ui/buttons/iconButtons/minusButton';
import { TNewValue } from 'shared/types';

interface Option {
	value: string;
	label: string;
}

interface SelectValueProps {
	options?: Options<Option>;
	value?: Option | null;
	onChangeOption?: (newValue: TNewValue) => void;
	interval: number;
}

export const SelectTimeIncrement: FC<SelectValueProps> = React.memo(({
	options = [],
	value,
	onChangeOption,
	interval,
}) => {
	const [isOpen, setOpen] = useState(false);

	const selectOption = useCallback(
		(option: Option | null) => {
			onChangeOption?.(option);
		},
		[onChangeOption],
	);

	const timeToMinutes = (time: string) => {
		const [hours, minutes] = time.split(':').map(Number);
		return hours * 60 + minutes;
	};

	const values = options.map(option => timeToMinutes(option.value));
	const min = Math.min(...values);
	const max = Math.max(...values);

	const updateSelection = useCallback(
		(newValue: number) => {
			const newOption = options.find(
				option => timeToMinutes(option.value) === newValue,
			);
			selectOption(newOption || null);
			setOpen(false);
		},
		[options, selectOption],
	);

	const handleIncrement = useCallback(() => {
		if (!value) return;

		const currentValue = timeToMinutes(value.value);
		const nextValue = Math.min(currentValue + interval, max);
		updateSelection(nextValue);
	}, [value, max, updateSelection, interval]);

	const handleDecrement = useCallback(() => {
		if (!value) return;

		const currentValue = timeToMinutes(value.value);
		const prevValue = Math.max(currentValue - interval, min);
		updateSelection(prevValue);
	}, [value, min, updateSelection, interval]);

	const openMenu = useCallback(() => {
		setOpen(true);
	}, []);

	const closeMenu = useCallback(() => {
		setOpen(false);
	}, []);

	const CustomControl = (props: any) => {
		const { children } = props;

		return (
			<components.Control {...props} innerProps={{ ...props.innerProps }}>
				<MinusButton onClick={handleDecrement} />
				<div
					onClick={!window.matchMedia("(pointer: coarse)").matches ? openMenu : undefined}
					onTouchStart={openMenu}
					style={{ flex: 1, textAlign: 'center', cursor: 'pointer' }}
				>
					{children}
				</div>
				<PlusButton onClick={handleIncrement} />
			</components.Control>
		);
	};

	const CustomInput: any = (props: InputProps) => {
		const onInputChange = (newValue: string) => {
			let value = newValue.replace(/[^0-9]/g, '');
			if (value.startsWith('00')) {
				value = value.replace('00', '0');
			}

			if (value.length > 2 && !value.includes(':')) {
				value = value.slice(0, 2) + ':' + value.slice(2, 5);
			}


			if (value.indexOf(':') > 0 && value.indexOf(':') !== 2) {
				return;
			}


			if (value.length === 6) {
				return;
			}

			props.selectProps.onInputChange(value, {} as any)
		}

		return (
			<components.Input {...props} value={props.value} onChange={(e) => onInputChange(e.target.value)} />
		);
	};

	return (
		<Select
			styles={selectStyles}
			value={value}
			onChange={selectOption}
			options={options}
			components={{
				Control: CustomControl,
				DropdownIndicator: null,
				Input: CustomInput,
			}}
			onMenuClose={closeMenu}
			menuIsOpen={isOpen}
		/>
	);
}, (prevProps, nextProps) => {
	if (prevProps.value === nextProps.value && prevProps.interval === nextProps.interval) {
		return true;
	}

	return false;
});
